import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import httpInstances from '@/api/axiosInstance';
import { detectEnvironment } from '@/utils/detectEnvironment';

type MarketsListState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled';
  error: string | null;
  marketsList: MarketsListItem[];
  groupedMarkets: MarketGroup[];
};

type ErrorResponse = {
  message: string;
};

const initialState: MarketsListState = {
  status: 'idle',
  error: null,
  marketsList: [],
  groupedMarkets: []
};

export interface MarketsListItem {
  marketId: string;
  metadata: {
    displayName: string;
    commodity: string;
    region: string;
  };
}

export type MarketGroup = {
  name: string;
  order: number;
  markets: { name: string; comingSoon?: boolean; tag?: string | null; marketId?: string }[];
};

interface MarketDTO {
  insightId: string;
  metadata: {
    displayName: string;
    commodity: string;
    region: string;
    tag?: string | null;
    comingSoon?: boolean;
  };
}

interface MarketGroupDTO {
  subHeading: string;
  order: number;
  markets: MarketDTO[];
}

export const getMarketsList = createAsyncThunk('getMarketsList', async (_, { rejectWithValue }) => {
  try {
    const { data } = await httpInstances.axiosInternalInstance.get<MarketGroupDTO[]>(
      `${detectEnvironment() === 'staging' ? '/staging/' : '/'}markets`
    );

    return {
      marketsList: data.reduce((acc, item) => {
        const { markets } = item;
        return [
          ...acc,
          ...markets.map(market => ({
            marketId: market.insightId.toLowerCase(),
            metadata: {
              displayName: market.metadata.displayName,
              commodity: market.metadata.commodity,
              region: market.metadata.region
            }
          }))
        ];
      }, [] as MarketsListItem[]),
      groupedMarkets: data.map(item => {
        const { subHeading, order, markets } = item;
        return {
          name: subHeading,
          order,
          markets: markets.map(market => ({
            name: market.metadata.displayName,
            tag: market.metadata.tag,
            comingSoon: market.metadata.comingSoon,
            marketId: market.insightId.toLowerCase()
          }))
        } as MarketGroup;
      })
    };
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      const errorResponse = error.response.data;
      return rejectWithValue(errorResponse);
    }
    throw error;
  }
});

const marketsListSlice = createSlice({
  name: 'marketsList',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getMarketsList.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getMarketsList.fulfilled, (state, action) => {
        state.status = 'fulfilled';

        state.marketsList = action.payload.marketsList;
        state.groupedMarkets = action.payload.groupedMarkets;
      })
      .addCase(getMarketsList.rejected, (state, action) => {
        state.status = 'failed';
        if (action.payload) {
          state.error = (action.payload as ErrorResponse).message ?? 'Request failed';
        } else {
          state.error = action.error.message ?? 'Request failed';
        }
      });
  }
});

export default marketsListSlice.reducer;
